'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Combobox } from '@/components/ui/combobox'
import { useCountries, useStates, useCities } from '@/hooks'
import { createAddress, updateAddress } from '@/lib/api'
import { getErrorMessage, setValidationErrors } from '@/lib/utils'
import { AddressType, CreateAddressParams } from '@/types/address'

type PropsType = {
  address?: AddressType
  formId?: string
  onSuccess?: (address: AddressType) => void
  onPendingChange?: (isPending: boolean) => void
}

const AddressForm = ({ address, formId, onSuccess, onPendingChange }: PropsType) => {
  const t = useTranslations()
  const queryClient = useQueryClient()
  const isEditing = !!address

  const [countryId, setCountryId] = useState<string | undefined>(address?.country?.id)
  const [stateId, setStateId] = useState<string | undefined>(address?.state?.id)

  const { data: countries, isLoading: loadingCountries } = useCountries()
  const { data: states, isLoading: loadingStates } = useStates(countryId)
  const { data: cities, isLoading: loadingCities } = useCities(stateId)

  // City is required only when the API returns a non-empty list for the selected state
  const hasCities = !!stateId && !loadingCities && (cities ?? []).length > 0
  const hasCitiesRef = useRef(hasCities)
  hasCitiesRef.current = hasCities

  const formSchema = useMemo(
    () =>
      z
        .object({
          title: z.string().min(1, t('required')),
          countryId: z.string().min(1, t('required')),
          stateId: z.string().min(1, t('required')),
          cityId: z.string().optional(),
          zipCode: z.string().min(1, t('required')),
          text: z.string().min(1, t('required')),
          primary: z.boolean()
        })
        .superRefine((data, ctx) => {
          if (hasCitiesRef.current && !data.cityId) {
            ctx.addIssue({ code: 'custom', message: t('required'), path: ['cityId'] })
          }
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: address?.title ?? '',
      countryId: address?.country?.id ?? '',
      stateId: address?.state?.id ?? '',
      cityId: address?.city?.id ?? '',
      zipCode: address?.zipCode ?? '',
      text: address?.text ?? '',
      primary: address?.primary ?? false
    }
  })

  const countryOptions = (countries ?? []).map(c => ({
    label: `${c.emoji} ${c.name}`,
    value: c.id,
    searchValue: c.name
  }))

  const stateOptions = (states ?? []).map(s => ({
    label: s.name,
    value: s.id,
    searchValue: s.name
  }))

  const cityOptions = (cities ?? []).map(c => ({
    label: c.name,
    value: c.id,
    searchValue: c.name
  }))

  // Reset state/city when country changes
  useEffect(() => {
    const sub = form.watch((value, { name }) => {
      if (name === 'countryId') {
        setCountryId(value.countryId || undefined)
        form.setValue('stateId', '')
        form.setValue('cityId', '')
        setStateId(undefined)
      }
      if (name === 'stateId') {
        setStateId(value.stateId || undefined)
        form.setValue('cityId', '')
      }
    })
    return () => sub.unsubscribe()
  }, [form])

  const { mutate, isPending } = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: CreateAddressParams = {
        title: values.title,
        countryId: values.countryId || undefined,
        stateId: values.stateId || undefined,
        cityId: values.cityId || undefined,
        zipCode: values.zipCode || undefined,
        text: values.text || undefined,
        primary: values.primary
      }
      return isEditing ? updateAddress(address.id, payload) : createAddress(payload)
    },
    onSuccess: async (data: AddressType) => {
      await queryClient.invalidateQueries({ queryKey: ['addresses'] })
      toast.success(isEditing ? t('address_updated_success') : t('address_created_success'))
      onSuccess?.(data)
    },
    onError: (error: any) => {
      if (error.response?.status === 422) {
        setValidationErrors(error, form.setError)
      } else {
        toast.error(getErrorMessage(error, t('something_went_wrong')))
      }
    }
  })

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(values => mutate(values))} className="flex flex-col gap-4">
        {/* Title */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="address-title" required>
                {t('title')}
              </FormLabel>
              <FormControl>
                <Input id="address-title" placeholder={t('address_title_placeholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Country / State / City / Zip Code */}
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="countryId"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="address-country" required>
                  {t('country')}
                </FormLabel>
                <FormControl>
                  <Combobox
                    id="address-country"
                    options={countryOptions}
                    value={field.value}
                    onValueChange={val => field.onChange(val)}
                    placeholder={t('select')}
                    disabled={loadingCountries}
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="stateId"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="address-state" required>
                  {t('state')}
                </FormLabel>
                <FormControl>
                  <Combobox
                    id="address-state"
                    options={stateOptions}
                    value={field.value}
                    onValueChange={val => field.onChange(val)}
                    placeholder={t('select')}
                    disabled={!countryId || loadingStates}
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cityId"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="address-city" required={hasCities}>
                  {t('city')}
                </FormLabel>
                <FormControl>
                  <Combobox
                    id="address-city"
                    options={cityOptions}
                    value={field.value}
                    onValueChange={val => field.onChange(val)}
                    placeholder={t('select')}
                    disabled={!stateId || loadingCities}
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="address-zip" required>
                  {t('zip_code')}
                </FormLabel>
                <FormControl>
                  <Input id="address-zip" placeholder={t('address_zip_placeholder')} {...field} maxLength={12} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Address Text */}
        <FormField
          control={form.control}
          name="text"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="address-text" required>
                {t('address')}
              </FormLabel>
              <FormControl>
                <Textarea id="address-text" placeholder={t('address_text_placeholder')} rows={3} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Primary */}
        <FormField
          control={form.control}
          name="primary"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-3">
              <FormControl>
                <Switch id="address-primary" checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel htmlFor="address-primary" className="cursor-pointer">
                {t('set_as_primary')}
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  )
}

export default AddressForm
